// PATH: src/components/modules/devtools/planner/PlannerFileTriageDialog.js

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
  Alert,
  TextField, // NEW (minimal): filter for large "all files" list
} from "@mui/material";

const PlannerFileTriageDialog = ({
  open,
  onClose,
  module,
  goalText,
  topN = 30,
  projectId = null, // optional, not required for metadata triage
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [triage, setTriage] = useState(null);

  // selected map: { [path]: true|false }
  const [selected, setSelected] = useState({});

  // NEW: filter for the large list
  const [filterText, setFilterText] = useState("");

  const allPaths = useMemo(() => {
    // NEW: prefer all_files if provided, else fall back to all
    const arr = triage?.all_files || triage?.all || [];
    return arr.map((x) => x?.path).filter(Boolean);
  }, [triage]);

  const load = async () => {
    setLoading(true);
    setErr("");
    setTriage(null);
    setSelected({});
    setFilterText(""); // NEW: reset filter on refresh/load
    try {
      const res = await fetch("/api/dev-ide/planner/triage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          module,
          goal_text: goalText,
          top_n: topN,
          use_llm: true,
          model: "gpt-5",
          project_id: projectId || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      const t = data?.data || null;
      setTriage(t);

      // Default selection:
      // - Select all must_touch + likely_touch by default
      const must = (t?.must_touch || []).map((x) => x?.path).filter(Boolean);
      const likely = (t?.likely_touch || []).map((x) => x?.path).filter(Boolean);

      const next = {};
      [...must, ...likely].forEach((p) => {
        next[p] = true;
      });
      setSelected(next);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const toggle = (path) => {
    setSelected((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  const selectAll = () => {
    const next = {};
    allPaths.forEach((p) => (next[p] = true));
    setSelected(next);
  };

  const clearAll = () => setSelected({});

  const confirm = () => {
    const chosen = Object.keys(selected).filter((k) => selected[k]);
    onConfirm?.(chosen);
  };

  const renderBucket = (title, items) => {
    const arr = Array.isArray(items) ? items : [];
    if (!arr.length) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Paper variant="outlined" sx={{ p: 1 }}>
          {arr.map((it) => {
            const p = it?.path;
            if (!p) return null;
            const reason = it?.reason || "";
            return (
              <Box key={p} sx={{ display: "flex", gap: 1, alignItems: "flex-start", py: 0.5 }}>
                <Checkbox checked={Boolean(selected[p])} onChange={() => toggle(p)} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {p}
                  </Typography>
                  {reason ? (
                    <Typography variant="caption" color="text.secondary">
                      {reason}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Paper>
      </Box>
    );
  };

  const renderAll = () => {
    // NEW: prefer full list if backend provides it
    const baseArr = triage?.all_files || triage?.all || [];
    if (!baseArr.length) return null;

    const ft = String(filterText || "").trim().toLowerCase();
    const arr = ft
      ? baseArr.filter((it) => {
          const p = String(it?.path || "").toLowerCase();
          const desc = String(it?.description || "").toLowerCase();
          const fileType = String(it?.file_type || it?.fileType || "").toLowerCase();
          return p.includes(ft) || desc.includes(ft) || fileType.includes(ft);
        })
      : baseArr;

    const label = triage?.all_files ? "All project files (backup)" : "All candidates (metadata)";

    return (
      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="subtitle2">
            {label}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 12 }} />
          <TextField
            size="small"
            label="Filter"
            type="search"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            sx={{ minWidth: 260 }}
          />
          {filterText ? (
            <Button size="small" onClick={() => setFilterText("")} aria-label="Clear filter" title="Clear filter">
              X
            </Button>
          ) : null}
        </Stack>

        <Paper variant="outlined" sx={{ p: 1, maxHeight: 260, overflow: "auto" }}>
          {arr.map((it) => {
            const p = it?.path;
            if (!p) return null;
            const desc = it?.description || "";
            const ft2 = it?.file_type || it?.fileType || "";
            return (
              <Box key={p} sx={{ display: "flex", gap: 1, alignItems: "flex-start", py: 0.5 }}>
                <Checkbox checked={Boolean(selected[p])} onChange={() => toggle(p)} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {p}
                  </Typography>
                  {(ft2 || desc) ? (
                    <Typography variant="caption" color="text.secondary">
                      {ft2 ? `type:${ft2} ` : ""}
                      {desc ? `— ${String(desc).slice(0, 240)}` : ""}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Paper>

        {/* NEW: tiny helper text so user knows filtering is applied */}
        {ft ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            Showing {arr.length} / {baseArr.length}
          </Typography>
        ) : null}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select files to provide as LLM context</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Goal: {String(goalText || "").slice(0, 180)}
        </Typography>

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Building candidate list + recommendations…</Typography>
          </Stack>
        )}

        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {err}
          </Alert>
        )}

        {triage?.llm_summary ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {triage.llm_summary}
          </Alert>
        ) : null}

        {renderBucket("Must touch (recommended)", triage?.must_touch)}
        {renderBucket("Likely touch (recommended)", triage?.likely_touch)}
        {renderBucket("Maybe touch", triage?.maybe_touch)}

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" onClick={selectAll} disabled={loading}>
            Select All
          </Button>
          <Button variant="outlined" onClick={clearAll} disabled={loading}>
            Clear All
          </Button>
          <Button variant="outlined" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>

        {renderAll()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          size="small"
          onClick={async () => {
            try {
              const chosen = Object.keys(selected).filter((k) => selected[k]);
              const payload = {
                goal_text: goalText,
                project_id: projectId || undefined,
                selected_files: chosen,
              };
              if (typeof topN !== "undefined") payload.top_n = topN;
              if (triage && typeof triage.max_tokens !== "undefined") payload.max_tokens = triage.max_tokens;
              await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
              window.alert("Copied Run LLM JSON");
            } catch (e) {
              window.alert("Failed to copy JSON");
            }
          }}
          disabled={loading}
        >
          Copy Run LLM JSON
        </Button>
        <Button variant="contained" onClick={confirm} disabled={loading}>
          Confirm Selection & Run LLM
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlannerFileTriageDialog;